import type { CanvasLayer, FrameContext } from "./canvasLayer";
import type { XY } from "../engine/xy";
import { dpr } from "./dpr";
import {
	SIGN_BUBBLE_BG_COLOR,
	SIGN_BUBBLE_BORDER_COLOR,
	SIGN_BUBBLE_BORDER_RADIUS,
	SIGN_BUBBLE_FADE_IN_MS,
	SIGN_BUBBLE_FADE_OUT_MS,
	SIGN_BUBBLE_FONT_SIZE,
	SIGN_BUBBLE_GAP_FROM_CELL,
	SIGN_BUBBLE_LINE_HEIGHT,
	SIGN_BUBBLE_MAX_WIDTH,
	SIGN_BUBBLE_MIN_WIDTH,
	SIGN_BUBBLE_PADDING_X,
	SIGN_BUBBLE_PADDING_Y,
	SIGN_BUBBLE_TAIL_HEIGHT,
	SIGN_BUBBLE_TAIL_WIDTH,
	SIGN_BUBBLE_TEXT_COLOR,
	SIGN_BUBBLE_VIEWPORT_MARGIN,
} from "./signBubbleConstants";

type BubbleState = "fadingIn" | "visible" | "fadingOut";

interface ActiveBubble {
	xy: XY;
	text: string;
	state: BubbleState;
	fadeMs: number;
}

export class SignBubbleRenderer implements CanvasLayer {
	private active: ActiveBubble | null = null;

	show(xy: XY, text: string): void {
		this.active = { xy, text, state: "fadingIn", fadeMs: 0 };
	}

	dismiss(): void {
		if (this.active === null) return;
		if (this.active.state === "fadingOut") return;
		this.active.state = "fadingOut";
		this.active.fadeMs = 0;
	}

	isOpen(): boolean {
		return this.active !== null && this.active.state !== "fadingOut";
	}

	hasContent(): boolean {
		return this.active !== null;
	}

	passTime(elapsedMs: number): void {
		if (this.active === null) return;
		const m = this.active;
		if (m.state === "fadingIn") {
			m.fadeMs += elapsedMs;
			if (m.fadeMs >= SIGN_BUBBLE_FADE_IN_MS) {
				m.state = "visible";
				m.fadeMs = 0;
			}
		} else if (m.state === "fadingOut") {
			m.fadeMs += elapsedMs;
			if (m.fadeMs >= SIGN_BUBBLE_FADE_OUT_MS) {
				this.active = null;
			}
		}
	}

	clear(): void {
		this.active = null;
	}

	render(ctx: CanvasRenderingContext2D, frame: FrameContext): void {
		if (this.active === null) return;
		const m = this.active;
		const camera = frame.camera;

		const ratio = dpr();
		const fontSize = Math.round(SIGN_BUBBLE_FONT_SIZE * ratio);
		const lineHeight = Math.round(SIGN_BUBBLE_LINE_HEIGHT * ratio);
		const paddingX = Math.round(SIGN_BUBBLE_PADDING_X * ratio);
		const paddingY = Math.round(SIGN_BUBBLE_PADDING_Y * ratio);
		const maxWidth = Math.round(SIGN_BUBBLE_MAX_WIDTH * ratio);
		const minWidth = Math.round(SIGN_BUBBLE_MIN_WIDTH * ratio);
		const borderRadius = Math.round(SIGN_BUBBLE_BORDER_RADIUS * ratio);
		const tailWidth = Math.round(SIGN_BUBBLE_TAIL_WIDTH * ratio);
		const tailHeight = Math.round(SIGN_BUBBLE_TAIL_HEIGHT * ratio);
		const gap = Math.round(SIGN_BUBBLE_GAP_FROM_CELL * ratio);
		const margin = Math.round(SIGN_BUBBLE_VIEWPORT_MARGIN * ratio);

		ctx.save();
		ctx.font = `${fontSize}px monospace`;
		ctx.textAlign = "left";
		ctx.textBaseline = "top";

		const lines = wrapText(ctx, m.text, maxWidth - paddingX * 2);
		let textBlockWidth = 0;
		for (const line of lines) {
			const w = ctx.measureText(line).width;
			if (w > textBlockWidth) textBlockWidth = w;
		}
		const bgWidth = Math.max(Math.min(textBlockWidth + paddingX * 2, maxWidth), minWidth);
		const bgHeight = lines.length * lineHeight + paddingY * 2;

		const cellCenter = camera.getCellCenterPixel(m.xy);
		const cellTop = cellCenter.y - camera.cellHeight / 2;
		const cellBottom = cellCenter.y + camera.cellHeight / 2;

		// Default placement: above the cell. Tail points down from bubble bottom.
		let pointsDown = true;
		let bgY = cellTop - gap - tailHeight - bgHeight;

		// Edge flip: if clipped at top, place below the cell.
		if (bgY < margin) {
			pointsDown = false;
			bgY = cellBottom + gap + tailHeight;
		}

		// Horizontal positioning: centered on cell.
		let bgX = Math.round(cellCenter.x - bgWidth / 2);

		// Edge flip: shift left/right if clipped.
		if (bgX < margin) bgX = margin;
		if (bgX + bgWidth > camera.viewportWidth - margin) {
			bgX = camera.viewportWidth - margin - bgWidth;
		}

		// Tail anchor on the bubble edge: pinned to the cell's x, clamped within the bubble width
		// (with a small inset so the tail base doesn't run off the corner).
		const tailInset = borderRadius + tailWidth / 2;
		let tailCenterX = Math.round(cellCenter.x);
		if (tailCenterX < bgX + tailInset) tailCenterX = Math.round(bgX + tailInset);
		if (tailCenterX > bgX + bgWidth - tailInset) tailCenterX = Math.round(bgX + bgWidth - tailInset);

		const alpha = this.computeAlpha();
		ctx.globalAlpha = alpha;

		// --- Draw bubble + tail as one filled shape ---
		ctx.fillStyle = SIGN_BUBBLE_BG_COLOR;
		ctx.strokeStyle = SIGN_BUBBLE_BORDER_COLOR;
		ctx.lineWidth = Math.max(1, Math.round(1 * ratio));

		drawBubbleShape(
			ctx,
			bgX,
			bgY,
			bgWidth,
			bgHeight,
			borderRadius,
			pointsDown,
			tailCenterX,
			tailWidth,
			tailHeight,
			cellCenter.y,
		);
		ctx.fill();
		ctx.stroke();

		// --- Draw text ---
		ctx.fillStyle = SIGN_BUBBLE_TEXT_COLOR;
		const textStartX = bgX + paddingX;
		let textY = bgY + paddingY;
		for (const line of lines) {
			ctx.fillText(line, textStartX, textY);
			textY += lineHeight;
		}

		ctx.restore();
	}

	private computeAlpha(): number {
		if (this.active === null) return 0;
		const m = this.active;
		if (m.state === "fadingIn") {
			if (SIGN_BUBBLE_FADE_IN_MS <= 0) return 1;
			return Math.min(1, m.fadeMs / SIGN_BUBBLE_FADE_IN_MS);
		}
		if (m.state === "fadingOut") {
			if (SIGN_BUBBLE_FADE_OUT_MS <= 0) return 0;
			return Math.max(0, 1 - m.fadeMs / SIGN_BUBBLE_FADE_OUT_MS);
		}
		return 1;
	}
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
	const words = text.split(/\s+/);
	const lines: string[] = [];
	let current = "";
	for (const word of words) {
		const candidate = current.length === 0 ? word : current + " " + word;
		if (ctx.measureText(candidate).width <= maxWidth) {
			current = candidate;
		} else {
			if (current.length > 0) lines.push(current);
			// If a single word is wider than maxWidth, hard-break it.
			if (ctx.measureText(word).width > maxWidth) {
				let buffer = "";
				for (const ch of word) {
					if (ctx.measureText(buffer + ch).width > maxWidth) {
						if (buffer.length > 0) lines.push(buffer);
						buffer = ch;
					} else {
						buffer += ch;
					}
				}
				current = buffer;
			} else {
				current = word;
			}
		}
	}
	if (current.length > 0) lines.push(current);
	if (lines.length === 0) lines.push("");
	return lines;
}

function drawBubbleShape(
	ctx: CanvasRenderingContext2D,
	x: number,
	y: number,
	w: number,
	h: number,
	r: number,
	pointsDown: boolean,
	tailCenterX: number,
	tailWidth: number,
	tailHeight: number,
	cellCenterY: number,
): void {
	// Builds a rounded rect path with a triangular tail integrated into the bottom or top edge.
	const left = x;
	const right = x + w;
	const top = y;
	const bottom = y + h;
	const radius = Math.min(r, Math.min(w, h) / 2);

	ctx.beginPath();
	if (pointsDown) {
		// Top edge: TL corner -> TR corner
		ctx.moveTo(left + radius, top);
		ctx.lineTo(right - radius, top);
		ctx.arcTo(right, top, right, top + radius, radius);
		// Right edge: down to BR corner
		ctx.lineTo(right, bottom - radius);
		ctx.arcTo(right, bottom, right - radius, bottom, radius);
		// Bottom edge: right portion, tail, left portion
		const tailLeft = Math.max(left + radius, tailCenterX - tailWidth / 2);
		const tailRight = Math.min(right - radius, tailCenterX + tailWidth / 2);
		ctx.lineTo(tailRight, bottom);
		ctx.lineTo(tailCenterX, cellCenterY);
		ctx.lineTo(tailLeft, bottom);
		ctx.lineTo(left + radius, bottom);
		// BL corner -> TL corner
		ctx.arcTo(left, bottom, left, bottom - radius, radius);
		ctx.lineTo(left, top + radius);
		ctx.arcTo(left, top, left + radius, top, radius);
	} else {
		// Tail points up. Build the path with tail integrated into the top edge.
		// BL corner -> BR corner
		ctx.moveTo(left + radius, bottom);
		ctx.arcTo(left, bottom, left, bottom - radius, radius);
		ctx.lineTo(left, top + radius);
		ctx.arcTo(left, top, left + radius, top, radius);
		// Top edge: left portion, tail, right portion
		const tailLeft = Math.max(left + radius, tailCenterX - tailWidth / 2);
		const tailRight = Math.min(right - radius, tailCenterX + tailWidth / 2);
		ctx.lineTo(tailLeft, top);
		ctx.lineTo(tailCenterX, cellCenterY);
		ctx.lineTo(tailRight, top);
		ctx.lineTo(right - radius, top);
		ctx.arcTo(right, top, right, top + radius, radius);
		ctx.lineTo(right, bottom - radius);
		ctx.arcTo(right, bottom, right - radius, bottom, radius);
		ctx.closePath();
	}
	ctx.closePath();
}
